import { Close } from '@mui/icons-material';
import {
  AppBar,
  Dialog,
  DialogContent,
  Toolbar,
  Stack,
  IconButton,
  Button,
  Select,
  MenuItem,
  Snackbar,
  Alert,
} from '@mui/material';
import { useEffect, useState } from 'react';
import { DEFAULT_QUESTION } from '../../../../../components/Questionnaire/constants';
import { addOrUpdateQuestionnaire } from '../../../../../apis/questionnaireApis';
import { useParams } from 'react-router-dom/cjs/react-router-dom.min';
import { useTranslation } from 'react-i18next';
import { createRenderSections, getFormQuestionsSubmitPayload, validateQuestionnaireQuestionForm } from '../utils';
import { useDispatch, useSelector } from 'react-redux';
import { questionnaireTemplateDetailsActions } from '../../../../../_actions';
import QuestionnaireQuestionForm from '../../../QuestionnaireQuestionForm/QuestionnaireQuestionForm';
import Loader from '../../../../../components/Loader';
import EllipsisTypography from '../../../../../components/EllipsisTypography';

const QuestionnaireFormDialog = ({ handleClose }) => {
  const { t } = useTranslation()
  const dispatch = useDispatch()
  const { questionnaireStore: { questionnaireTemplateDetails: { templateDetails } } } = useSelector(state => state);
  const latestVersionNumber = Math.max(...Object.values(templateDetails).map(v => v.version));
  const [selectedVersion, setSelectedVersion] = useState(latestVersionNumber);
  const [sections, setSections] = useState([DEFAULT_QUESTION]);
  const { questionnaireTemplateId  } = useParams();
  const [activeToolbarKey, setActiveToolbarKey] = useState("0-0")
  const [questionFormErrors, setQuestionFormErrors] = useState({})
  const [loading, setLoading] = useState()
  const [toast, setToast] = useState({
          open: false,
          message: "",
          type: "error"
  })
  const currentVersionDetails = templateDetails[selectedVersion] ;
  
  const handleSave = async () => {
    const errors = validateQuestionnaireQuestionForm(sections, t)
    if (Object.keys(errors).length > 0) {
      setQuestionFormErrors(errors)
      setToast({
        open: true,
        message: t("questionnaire.template.validationError"),
        type: "error",
      });
      return
    };

    try {
      await addOrUpdateQuestionnaire(questionnaireTemplateId , getFormQuestionsSubmitPayload(sections));
      dispatch(questionnaireTemplateDetailsActions.fetchQuestionnaireTemplateDetails(questionnaireTemplateId ))
      handleClose()
    } catch (err) {
      console.error("Error saving questionnaire:", err);
    }
  };

  const handleVersionChange = (e) => {
    const versionName = e.target.value;
    setSelectedVersion(versionName);

    const versionObj = templateDetails[latestVersionNumber]?.versions.find(
      (v) => v.name === versionName
    );
    setLoading(true);
    dispatch(
      questionnaireTemplateDetailsActions.fetchQuestionnaireTemplateDetails(
        questionnaireTemplateId,
        versionObj?.id
      )
    ).finally(() => {
      setLoading(false);
    });
  };


  useEffect(() => {
    if (!currentVersionDetails?.sections) return;

    if (currentVersionDetails.sections.length > 0) {
      const mappedSections = createRenderSections(currentVersionDetails.sections);
      setSections(mappedSections);
      setActiveToolbarKey("0-0");
    } else {
      setSections([DEFAULT_QUESTION]);
    }
  }, [currentVersionDetails, selectedVersion]);


  return (
    <Dialog open={true} fullScreen>
      <AppBar position="relative">
        <Toolbar>
          <Stack
            direction="row"
            width="100%"
            alignItems="center"
            justifyContent="space-between"
          >
            <Stack direction="row" alignItems="center">
              <IconButton
                edge="start"
                color="inherit"
                onClick={handleClose}
                aria-label="close"
              >
                <Close />
              </IconButton>
              <EllipsisTypography component="div" variant={"h6"} sx={{ ml: 2 }} title={t('questionnaire.form')} />
            </Stack>

            <Stack direction="row" alignItems="center" spacing={2}>
              <Select
                value={selectedVersion || ""}
                onChange={handleVersionChange}
                variant="outlined"
                size="small"
                sx={{ bgcolor: "white", color: "black" }}
              >
                {(Array.isArray(currentVersionDetails?.versions)
                  ? [...currentVersionDetails.versions].reverse()
                  : []
                ).map((v) => (
                  <MenuItem key={v.id} value={v.name}>
                    V{v.name}
                  </MenuItem>
                ))}

              </Select>

              <Button sx={{ color: 'inherit' }}  disabled={selectedVersion != latestVersionNumber}
                onClick={handleSave}
              >
                {t('general.save')}
              </Button>
            </Stack>
          </Stack>
        </Toolbar>
      </AppBar>

      <DialogContent sx={{ p: 0 }}>
        {loading ? (
          <Stack
            width="100%"
            height="100%"
            justifyContent="center"
            alignItems="center"
            p={4}
          >
            <Loader/>
          </Stack>
        ) : (
          <QuestionnaireQuestionForm
            sections={sections}
            setSections={setSections}
            activeToolbarKey={activeToolbarKey}
            setActiveToolbarKey={setActiveToolbarKey}
            questionFormErrors={questionFormErrors}
            setQuestionFormErrors={setQuestionFormErrors}
            isReadOnly={selectedVersion != latestVersionNumber}

          />
        )}
        <Snackbar
          open={toast?.open}
          autoHideDuration={6000}
          onClose={() =>
            setToast({
              open: false,
              message: "",
              type: "error"
            })
          }
          anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
          key={toast.message}
          sx={{ zIndex: 2000 }}
        >
          <Alert
            onClose={() =>
              setToast({
                open: false,
                message: "",
                type: "error"
              })
            }
            severity={toast?.type}
          >
            {toast?.message}
          </Alert>
        </Snackbar>
      </DialogContent>

    </Dialog>
  );
};

export default QuestionnaireFormDialog;
